/**
 * Completion history (`phase-2d-management-history.md` §3, ADR-0011
 * Decision 2) -- a pure, read-only aggregation over the event log, grouped
 * by calendar month. No new event type, no stored per-month counters
 * (AC-M16): every call re-derives its answer from `(event log, month)` (or,
 * for the year summary, `(event log, throughMonth)`).
 *
 * Two entry points mirroring the two-tier contract split:
 * - `computeCompletionHistoryMonth` -- full detail for one month
 *   (`CompletionHistoryMonthView`), the History screen's primary view.
 * - `computeCompletionHistoryYearSummary` -- lightweight per-month
 *   `hasActivity` signal for the year strip, spanning every month from the
 *   earliest event through `throughMonth` inclusive. Deliberately NOT N
 *   calls to the month function (ADR-0011: "a dedicated summary query is one
 *   document-full-log-scan, not twelve") -- it scans the log once and
 *   buckets by month directly.
 *
 * Reuses existing state/projection helpers rather than re-deriving
 * objective/habit state a second time (ADR-0011 Consequences):
 * `buildObjectiveStates`/`computeActionCompletionStatus` for objectives,
 * `buildHabitStates` + `excludingReverted`/`eventsOfTypes`/`weekOf`/
 * `isInWeek` for habits.
 */

import type {
  CompassEvent,
  CompletedObjectiveView,
  CompletionHistoryMonthSummaryView,
  CompletionHistoryMonthView,
  HabitHistoryEntryView,
  ObjectiveActionsCompletedView,
  ObjectiveId,
  RealismAdjustmentHistoryEntryView,
  YearMonth,
} from '../../contracts';
import { eventsOfTypes, excludingReverted } from '../event-log/event-log';
import { buildHabitStates } from '../habits/state';
import { isInWeek, weekOf } from '../habits/week';
import {
  buildObjectiveStates,
  computeActionCompletionStatus,
} from '../objectives/state';

/** Truncate an `IsoDate`-shaped string ('YYYY-MM-DD') to its `YearMonth` ('YYYY-MM') -- the one, ADR-0011-mandated derivation. Never computed any other way. */
function monthOf(isoDate: string): YearMonth {
  return isoDate.slice(0, 7) as YearMonth;
}

/**
 * Full detail for one calendar month (spec §3.2 MC-11). Pure function of
 * `(event log, month)`.
 */
export function computeCompletionHistoryMonth(
  events: readonly CompassEvent[],
  month: YearMonth,
): CompletionHistoryMonthView {
  return {
    month,
    finishedObjectives: computeFinishedObjectives(events, month),
    objectiveActionsCompleted: computeObjectiveActionsCompleted(events, month),
    habitActivity: computeHabitActivity(events, month),
    realismAdjustments: computeRealismAdjustments(events, month),
  };
}

/**
 * Lightweight per-month activity signal for the year strip (spec §3.2),
 * spanning every month from the earliest event's month through
 * `throughMonth` inclusive. One pass over the log, not N calls to
 * `computeCompletionHistoryMonth` (ADR-0011 "why not derive the year strip
 * from N calls").
 */
export function computeCompletionHistoryYearSummary(
  events: readonly CompassEvent[],
  throughMonth: YearMonth,
): readonly CompletionHistoryMonthSummaryView[] {
  const activeMonths = collectActiveMonths(events, throughMonth);

  if (activeMonths.size === 0) {
    // No events at all: an empty range has nothing to summarize -- the
    // caller (UI) decides how to render "no history yet" for the strip
    // itself; this function's job is just "which months have activity",
    // and there are none.
    return [];
  }

  const earliestMonth = [...activeMonths].sort()[0] as YearMonth;
  const months = monthRange(earliestMonth, throughMonth);

  return months.map((month) => ({
    month,
    hasActivity: activeMonths.has(month),
  }));
}

// ---------------------------------------------------------------------------
// Finished objectives (spec §3.1 item 1)
// ---------------------------------------------------------------------------

function computeFinishedObjectives(
  events: readonly CompassEvent[],
  month: YearMonth,
): readonly CompletedObjectiveView[] {
  const states = buildObjectiveStates(events);
  const completion = computeActionCompletionStatus(events);

  const results: CompletedObjectiveView[] = [];
  for (const state of states.values()) {
    const liveActionIds = state.order.filter((actionId) => {
      const action = state.actionsById.get(actionId);
      return action !== undefined && !action.removed;
    });
    if (liveActionIds.length === 0) continue;

    const completedOnDates: string[] = [];
    let allComplete = true;
    for (const actionId of liveActionIds) {
      const status = completion.get(actionId);
      if (!status) {
        allComplete = false;
        break;
      }
      completedOnDates.push(status.completedOn);
    }
    if (!allComplete) continue;

    // "Finished" = every live action complete; the finish date is the max
    // (latest) completion date among them (spec §3.1 item 1 / ADR-0011).
    const finishedOn = completedOnDates.sort().at(-1);
    if (!finishedOn || monthOf(finishedOn) !== month) continue;

    results.push({
      objectiveId: state.objectiveId,
      title: state.title,
      finishedOn: finishedOn as CompletedObjectiveView['finishedOn'],
      actionsTotal: liveActionIds.length,
    });
  }

  return results.sort((a, b) => (a.objectiveId < b.objectiveId ? -1 : a.objectiveId > b.objectiveId ? 1 : 0));
}

// ---------------------------------------------------------------------------
// Completed actions per objective per month (spec §3.1 item 2)
// ---------------------------------------------------------------------------

function computeObjectiveActionsCompleted(
  events: readonly CompassEvent[],
  month: YearMonth,
): readonly ObjectiveActionsCompletedView[] {
  const states = buildObjectiveStates(events);
  const activeCompletions = eventsOfTypes(excludingReverted(events), [
    'action.completed',
  ]);

  const countByObjective = new Map<ObjectiveId, number>();
  for (const event of activeCompletions) {
    if (monthOf(event.occurredOn) !== month) continue;
    countByObjective.set(
      event.objectiveId,
      (countByObjective.get(event.objectiveId) ?? 0) + 1,
    );
  }

  const results: ObjectiveActionsCompletedView[] = [];
  for (const [objectiveId, actionsCompleted] of countByObjective) {
    const state = states.get(objectiveId);
    // A completion for an objective no longer resolvable (shouldn't happen
    // -- objectives are never hard-deleted, ADR-0011/spec §1.1) is skipped
    // defensively rather than surfaced with a missing title.
    if (!state) continue;
    results.push({ objectiveId, title: state.title, actionsCompleted });
  }

  return results.sort((a, b) => (a.objectiveId < b.objectiveId ? -1 : a.objectiveId > b.objectiveId ? 1 : 0));
}

// ---------------------------------------------------------------------------
// Habit activity (spec §3.1 item 3) -- plain counts, never percentages
// ---------------------------------------------------------------------------

function computeHabitActivity(
  events: readonly CompassEvent[],
  month: YearMonth,
): readonly HabitHistoryEntryView[] {
  const states = buildHabitStates(events);
  const active = excludingReverted(events);
  const activeCompletions = eventsOfTypes(active, ['habit.completed']);
  const activeLapses = eventsOfTypes(active, ['habit.lapse_logged']);

  const results: HabitHistoryEntryView[] = [];
  for (const state of states.values()) {
    if (state.plan.habitKind === 'build') {
      const completions = activeCompletions.filter(
        (e) => e.habitId === state.habitId && monthOf(e.occurredOn) === month,
      ).length;
      if (completions === 0) continue;
      results.push({
        habitKind: 'build',
        habitId: state.habitId,
        name: state.name,
        category: state.category,
        completions,
      });
    } else {
      const cleanWeeks = countCleanWeeksInMonth(
        activeLapses.filter((e) => e.habitId === state.habitId).map((e) => e.occurredOn),
        month,
      );
      if (cleanWeeks === 0) continue;
      results.push({
        habitKind: 'avoid',
        habitId: state.habitId,
        name: state.name,
        category: state.category,
        cleanWeeks,
      });
    }
  }

  return results.sort((a, b) => (a.habitId < b.habitId ? -1 : a.habitId > b.habitId ? 1 : 0));
}

/**
 * Count of Mon-Sun weeks, among the weeks that belong to `month` (a week
 * belongs to the month of its Monday, ADR-0011), that have zero lapses among
 * `lapseDates`. Only weeks with at least one day inside `month` overall are
 * NOT considered here on purpose -- the ADR's rule is specifically "windowed
 * ... where the week is considered part of the month by its Monday's month",
 * so a week whose Monday falls in the prior month (even if it spills a few
 * days into this one) is NOT this month's to count, and a week whose Monday
 * is in this month but spills into the next IS fully this month's.
 */
function countCleanWeeksInMonth(lapseDates: readonly string[], month: YearMonth): number {
  const weekStarts = mondaysInMonth(month);
  let clean = 0;
  for (const weekStart of weekStarts) {
    const hasLapse = lapseDates.some((date) => isInWeek(date, weekStart));
    if (!hasLapse) clean += 1;
  }
  return clean;
}

/** Every Monday (`WeekStart`) whose own calendar month is `month`. */
function mondaysInMonth(month: YearMonth): readonly ReturnType<typeof weekOf>[] {
  const [yearStr, monthStr] = month.split('-');
  const year = Number(yearStr);
  const monthIndex = Number(monthStr) - 1; // 0-based
  const daysInMonth = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();

  const mondays: ReturnType<typeof weekOf>[] = [];
  for (let day = 1; day <= daysInMonth; day += 1) {
    const iso = `${yearStr}-${monthStr}-${day.toString().padStart(2, '0')}`;
    const start = weekOf(iso);
    // weekOf(iso)'s own month, not iso's month -- a Monday in the middle of
    // this month always has itself as its own weekOf result, so this check
    // is really "is this candidate Monday itself inside `month`", collecting
    // each qualifying Monday exactly once even though the daily loop above
    // revisits it up to 7 times.
    if (monthOf(start) === month && !mondays.includes(start)) {
      mondays.push(start);
    }
  }
  return mondays;
}

// ---------------------------------------------------------------------------
// Realism adjustments (spec §3.1 item 4) -- both sources, unfiltered
// ---------------------------------------------------------------------------

function computeRealismAdjustments(
  events: readonly CompassEvent[],
  month: YearMonth,
): readonly RealismAdjustmentHistoryEntryView[] {
  const objectiveStates = buildObjectiveStates(events);
  const habitStates = buildHabitStates(events);
  const reverted = new Set(
    eventsOfTypes(events, ['log_entry.reverted']).map((e) => e.revertedEventId),
  );

  const results: RealismAdjustmentHistoryEntryView[] = [];

  for (const event of eventsOfTypes(events, ['objective.target_date_adjusted'])) {
    if (reverted.has(event.eventId)) continue;
    if (monthOf(event.occurredOn) !== month) continue;
    const state = objectiveStates.get(event.objectiveId);
    if (!state) continue;
    results.push({
      kind: 'objective_date',
      objectiveId: event.objectiveId,
      title: state.title,
      previousTargetDate: event.previousTargetDate,
      nextTargetDate: event.nextTargetDate,
      source: event.source,
      eventId: event.eventId,
    });
  }

  for (const event of eventsOfTypes(events, ['habit.target_adjusted'])) {
    if (reverted.has(event.eventId)) continue;
    if (monthOf(event.occurredOn) !== month) continue;
    const state = habitStates.get(event.habitId);
    if (!state) continue;
    results.push({
      kind: 'habit_target',
      habitId: event.habitId,
      name: state.name,
      adjustment: event.adjustment,
      source: event.source,
      eventId: event.eventId,
    });
  }

  return results;
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/**
 * Every distinct `YearMonth` in which at least one of the four
 * `CompletionHistoryMonthView` categories would be non-empty -- the
 * condition `hasActivity` is defined as (ADR-0011). Computed by scanning the
 * relevant event types once and bucketing their `occurredOn` months,
 * short-circuiting per month rather than materializing full month detail.
 */
function collectActiveMonths(
  events: readonly CompassEvent[],
  throughMonth: YearMonth,
): ReadonlySet<YearMonth> {
  const months = new Set<YearMonth>();

  // Finished objectives and completed actions both derive from active
  // action.completed events -- every such event's month is a candidate for
  // "objectiveActionsCompleted" activity at minimum (a finished objective's
  // finish month is necessarily also a month with a completion in it).
  const active = excludingReverted(events);
  for (const event of eventsOfTypes(active, ['action.completed'])) {
    months.add(monthOf(event.occurredOn));
  }

  for (const event of eventsOfTypes(active, ['habit.completed'])) {
    months.add(monthOf(event.occurredOn));
  }

  const realismEventTypes = [
    'objective.target_date_adjusted',
    'habit.target_adjusted',
  ] as const;
  const reverted = new Set(
    eventsOfTypes(events, ['log_entry.reverted']).map((e) => e.revertedEventId),
  );
  for (const event of eventsOfTypes(events, realismEventTypes)) {
    if (reverted.has(event.eventId)) continue;
    months.add(monthOf(event.occurredOn));
  }

  // Avoid-habit clean weeks are activity too, even with zero
  // habit.clean_logged/habit.lapse_logged events in the month (an avoid
  // habit that existed through a whole clean week still counts, per
  // computeHabitActivity's avoid branch) -- so this cannot be derived from
  // "did any event occur in this month" alone. Bounded correctly: for each
  // avoid habit, walk every month from its creation month through
  // `throughMonth` (the caller's own upper bound -- normally "now"; a clean
  // week can extend arbitrarily past the last recorded event, so the log's
  // own latest event month is NOT a safe upper bound here, unlike the lower
  // bound) and test countCleanWeeksInMonth directly -- cheap at this
  // product's single-user, leave-length scale (a handful of habits, at most
  // a few dozen months).
  const habitStates = buildHabitStates(events);
  const avoidHabits = [...habitStates.values()].filter((s) => s.plan.habitKind === 'avoid');
  if (avoidHabits.length > 0) {
    const activeLapses = eventsOfTypes(excludingReverted(events), ['habit.lapse_logged']);
    for (const state of avoidHabits) {
      const lapseDates = activeLapses
        .filter((e) => e.habitId === state.habitId)
        .map((e) => e.occurredOn);
      const createdMonth = monthOf(state.createdOn);
      const through = createdMonth > throughMonth ? createdMonth : throughMonth;
      for (const month of monthRange(createdMonth, through)) {
        if (months.has(month)) continue;
        if (countCleanWeeksInMonth(lapseDates, month) > 0) {
          months.add(month);
        }
      }
    }
  }

  return months;
}

/** Every `YearMonth` from `from` through `through`, inclusive, ascending. */
function monthRange(from: YearMonth, through: YearMonth): readonly YearMonth[] {
  const months: YearMonth[] = [];
  let [year, month] = from.split('-').map(Number) as [number, number];
  const [throughYear, throughMonth] = through.split('-').map(Number) as [number, number];

  // Defensive: an empty/inverted range yields no months rather than looping
  // forever or throwing (throughMonth earlier than the earliest event month
  // shouldn't happen in practice -- callers pass "now" -- but this keeps the
  // function total).
  while (year < throughYear || (year === throughYear && month <= throughMonth)) {
    months.push(`${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}` as YearMonth);
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return months;
}
